import { BeeswaxReportProperties as ReportProperties } from '@/Enums/Report/BeeswaxReportProperties.enum';
import FootTrafficService from '@/Services/FootTraffic/FootTraffic.service';
import { footTrafficAdvertiserFilter } from '@/helpers/advertisers/advertiserFilters.helpers';
import { ribeyeAdvertiserMapper } from '@/helpers/advertisers/advertiserMappers.helpers';
import type { RootState } from '@/store';
import { createSelector } from '@reduxjs/toolkit';
import { selectSharedApiClients, selectSharedApiWhitelabels } from '../sharedApi.selectors';

export const selectFootTraffic = (state: RootState) => state.footTraffic;

export const selectFootTrafficFilters = createSelector([selectFootTraffic], ({ filters }) => filters);

export const selectFootTrafficShowLiftPercent = createSelector(
  [selectFootTrafficFilters],
  ({ advertisers }) => advertisers?.length === 1,
);

const selectFootTrafficVisitsReports = createSelector(
  [selectFootTraffic],
  ({
    footTrafficVisitsReportsRequestId,
    hourReport,
    weekdayReport,
    visitsHistory,
    historicalAverage,
    yoyPercentChange,
    dwellReport,
    freqReport,
    tradeAreaReport,
    competitiveRanking,
  }) => ({
    loading: !!footTrafficVisitsReportsRequestId,
    hourReport,
    weekdayReport,
    visitsHistory,
    historicalAverage,
    yoyPercentChange,
    dwellReport,
    freqReport,
    tradeAreaReport,
    competitiveRanking,
  }),
);

const selectFootTrafficImpressionsReports = createSelector(
  [selectFootTraffic],
  ({ footTrafficImpressionsReportsRequestId, dateReport, flightWindows, totals }) => ({
    loading: !!footTrafficImpressionsReportsRequestId,
    dateReport,
    flightWindows,
    totals,
  }),
);

export const selectFootTrafficTotalsChannels = createSelector([selectFootTraffic], ({ channels }) => channels);

export const selectFootTrafficTotals = createSelector(
  [selectFootTraffic, selectFootTrafficTotalsChannels, selectFootTrafficFilters],
  (
    {
      footTrafficImpressionsReportsRequestId,
      footTrafficVisitsReportsRequestId,
      totals,
      visitsHistory,
      yoyPercentChange,
      freqReport,
    },
    channels,
    filters,
  ) => ({
    loading: !!footTrafficImpressionsReportsRequestId || !!footTrafficVisitsReportsRequestId,
    totals: FootTrafficService.getTotals(
      totals,
      channels,
      visitsHistory,
      yoyPercentChange,
      freqReport,
      filters.dateRangeStart,
      filters.dateRangeEnd,
    ),
  }),
);

export const selectFootTrafficDateReport = createSelector(
  [selectFootTrafficImpressionsReports],
  ({ loading, dateReport, flightWindows }) => ({
    loading,
    dateReport,
    flightWindows,
  }),
);

export const selectFootTrafficOnAirOffAir = createSelector(
  [selectFootTrafficImpressionsReports],
  ({ loading, totals }) => ({
    loading,
    onAir: totals[ReportProperties.ON_AIR_AVG_WEEKLY_VISITS],
    offAir: totals[ReportProperties.OFF_AIR_AVG_WEEKLY_VISITS],
  }),
);

export const selectFootTrafficWeekdayReport = createSelector(
  [selectFootTrafficVisitsReports],
  ({ loading, weekdayReport }) => ({
    loading,
    weekdayReport,
  }),
);

export const selectFootTrafficHourOfDayReport = createSelector(
  [selectFootTrafficVisitsReports],
  ({ loading, hourReport }) => ({
    loading,
    hourReport,
  }),
);

export const selectFootTrafficVisitsHistory = createSelector(
  [selectFootTrafficVisitsReports],
  ({ loading, visitsHistory, historicalAverage, yoyPercentChange }) => ({
    loading,
    visitsHistory,
    historicalAverage,
    yoyPercentChange,
  }),
);

export const selectFootTrafficDwellReport = createSelector(
  [selectFootTrafficVisitsReports],
  ({ loading, dwellReport }) => ({
    loading,
    dwellReport,
  }),
);

export const selectFootTrafficFreqReport = createSelector(
  [selectFootTrafficVisitsReports],
  ({ loading, freqReport }) => ({
    loading,
    freqReport,
  }),
);

export const selectFootTrafficTradeAreaReport = createSelector(
  [selectFootTrafficVisitsReports],
  ({ loading, tradeAreaReport }) => ({
    loading,
    tradeAreaReport,
  }),
);

export const selectFootTrafficRanking = createSelector(
  [selectFootTrafficVisitsReports],
  ({ loading, competitiveRanking }) => ({
    loading,
    competitiveRanking,
  }),
);

export const selectFootTrafficCampaignFilterOptions = createSelector(
  [selectFootTraffic],
  ({ campaigns, campaignsRequestId }) => ({ campaigns, loading: !!campaignsRequestId }),
);

export const selectFootTrafficLocationFilterOptions = createSelector(
  [selectFootTraffic],
  ({ locations, locationsRequestId }) => ({ locations, loading: !!locationsRequestId }),
);

export const selectFootTrafficLineItemFilterOptions = createSelector(
  [selectFootTraffic],
  ({ lineItems, lineItemsRequestId }) => ({ lineItems, loading: !!lineItemsRequestId }),
);

export const selectFootTrafficVenueCoverage = createSelector(
  [selectFootTraffic],
  ({ venueCoverage, venueCoverageRequestId }) => ({
    summary: venueCoverage.summary,
    locations: venueCoverage.locations,
    loading: !!venueCoverageRequestId,
  }),
);

const footTrafficWhitelabelsSelector = (state: RootState) =>
  selectSharedApiWhitelabels(state, ribeyeAdvertiserMapper, footTrafficAdvertiserFilter);
const footTrafficClientsSelector = (state: RootState) =>
  selectSharedApiClients(state, ribeyeAdvertiserMapper, footTrafficAdvertiserFilter);

export const selectFootTrafficWhitelabels = createSelector(
  [footTrafficWhitelabelsSelector],
  ({ whitelabels }) => whitelabels,
);
export const selectFootTrafficClients = createSelector([footTrafficClientsSelector], ({ clients }) => clients);
